import * as RECORDER from '../types/recorder';
import { arrayBufferConcat, float32ToArrayBuffer, getBufferLength, addWavHeader } from '../util';
export class AbstractRecorder {
    constructor(option) {
        this.workerReady = false;
        this._totalTime = 0;
        this.needRecord = true;
        this.pcm = [];
        // 设置默认值
        this.option = Object.assign({
            sampleRate: 8000,
            inputChannel: RECORDER.Channel.Left,
            batch: 1,
            mp3BitRate: 128,
            mp3WorkerPathPrefix: '',
            mp3WorkerPath: 'js/mp3Worker.22401e3139a1c3c8e3e5.js',
            timeout: 300000
        }, option);
        let pathPrefix = this.option.mp3WorkerPathPrefix;
        if (pathPrefix !== '' && !pathPrefix.endsWith('/')) {
            pathPrefix += '/';
        }
        this.worker = new Worker(`${pathPrefix}${this.option.mp3WorkerPath}`, { name: this.workerName() });
        this.worker.onmessage = (event) => {
            var _a, _b, _c, _d, _e;
            if (event.data.cmd === 'ready') {
                this.workerReady = true;
            }
            else if (event.data.cmd === 'mp3' && event.data.data && this.workerHandleMp3) {
                this.workerHandleMp3(event.data.data.blob);
            }
            else if (event.data.cmd === 'getEur') {
                (_a = this.workerHandleGetEur) === null || _a === void 0 ? void 0 : _a.call(this, event.data.data);
            }
            else if (event.data.cmd === 'score') {
                this._score = (_c = (_b = event.data) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.score;
                this._bufferScore = (_e = (_d = event.data) === null || _d === void 0 ? void 0 : _d.data) === null || _e === void 0 ? void 0 : _e.bufferScore;
            }
            else {
                this.onMessage(event);
            }
        };
    }
    get totalTime() {
        return this._totalTime;
    }
    get score() {
        return this._score;
    }
    get bufferScore() {
        return this._bufferScore;
    }
    async destroy() {
        var _a;
        this.clearStatus();
        if (this.initTimeout) {
            clearTimeout(this.initTimeout);
        }
        (_a = this.worker) === null || _a === void 0 ? void 0 : _a.postMessage({
            cmd: 'destroy'
        });
        this.stopMediaStream();
        await this.closeAudioContext();
    }
    async init() {
        const p = new Promise((resolve, reject) => {
            let i;
            this.initTimeout = setTimeout(() => {
                if (i) {
                    clearInterval(i);
                }
                reject(new Error('worker not workerReady'));
            }, this.option.timeout);
            const checkWorkerReady = () => {
                if (this.workerReady) {
                    clearInterval(i);
                    clearTimeout(this.initTimeout);
                    resolve(undefined);
                }
            };
            checkWorkerReady();
            i = setInterval(checkWorkerReady, 100);
        });
        await p;
    }
    /**
     * 开始录音
     */
    async start() {
        var _a, _b;
        this._totalTime = 0;
        this.needRecord = true;
        this.pcm = [];
        this._score = undefined;
        this._bufferScore = undefined;
        this.postMessageInit();
        if (!this.context) {
            this.context = new window.AudioContext({
                sampleRate: this.option.sampleRate
            });
            this.stream = await navigator.mediaDevices.getUserMedia({
                audio: true
            });
            this.source = this.context.createMediaStreamSource(this.stream);
            // script processor
            this.scriptNode = this.context.createScriptProcessor(getBufferLength(this.option.sampleRate) / 2 * this.option.batch, 2, 1);
            this.scriptNode.onaudioprocess = (e) => {
                // 暂停/恢复
                if (!this.needRecord) {
                    return;
                }
                const inputBuffer = e.inputBuffer;
                // const outputBuffer = e.outputBuffer
                const inputData = inputBuffer.getChannelData(this.option.inputChannel);
                // const outputData = outputBuffer.getChannelData(0)
                this._totalTime += inputBuffer.duration;
                this.pcm.push(inputData.slice(0));
                const cloneBuffer = inputData.slice(0);
                this.worker.postMessage({
                    cmd: 'data',
                    data: cloneBuffer
                });
                this.onprogress && this.onprogress();
            };
            (_a = this.source) === null || _a === void 0 ? void 0 : _a.connect(this.scriptNode);
            (_b = this.scriptNode) === null || _b === void 0 ? void 0 : _b.connect(this.context.destination);
        }
    }
    /**
     * 停止录音
     */
    async stop() {
        this.stopMediaStream();
        await this.closeAudioContext();
    }
    /**
     * 暂停录音
     */
    pause() {
        this.needRecord = false;
    }
    /**
     * 从暂停中恢复录音
     */
    resume() {
        this.needRecord = true;
    }
    /**
     * 清除状态
     * @private
     */
    clearStatus() {
        this.workerReady = false;
        this._totalTime = 0;
        this.needRecord = true;
        this.pcm = [];
        this._score = undefined;
        this._bufferScore = undefined;
    }
    stopMediaStream() {
        if (this.stream && this.stream.getTracks) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = undefined;
        }
    }
    async closeAudioContext() {
        if (this.context && this.context.close && this.context.state !== 'closed') {
            await this.context.close();
            this.context = undefined;
        }
    }
    /**
     * @deprecated
     */
    getScore() {
        return this._score;
    }
    /**
     * 获取 pcm 数据 Uint8Array
     */
    getPcm() {
        const buffer = [];
        for (let i = 0; i < this.pcm.length; i++) {
            buffer.push(float32ToArrayBuffer(this.pcm[i]));
        }
        return arrayBufferConcat(buffer);
    }
    /**
     * 获取wave
     */
    getWav() {
        return addWavHeader(this.getPcm(), this.option.sampleRate, 16, 1);
    }
    /**
     * 获取 mp3 Blob
     * @return
     */
    async getMp3() {
        return new Promise((resolve, reject) => {
            this.worker.postMessage({
                cmd: 'flush',
                data: {}
            });
            // timeout reject
            const ti = setTimeout(() => {
                reject(new Error('Get mp3 timeout!'));
            }, 10000);
            // resolve
            this.workerHandleMp3 = (blob) => {
                clearTimeout(ti);
                resolve(blob);
            };
        });
    }
    /**
     * 获取eur信息
     */
    async getEur(partName) {
        return new Promise((resolve, reject) => {
            // timeout reject
            const ti = setTimeout(() => {
                reject(new Error('getEur timeout!'));
            }, 3000);
            this.workerHandleGetEur = (data) => {
                clearTimeout(ti);
                resolve(data);
            };
            this.worker.postMessage({
                cmd: 'getEur',
                data: {
                    partName
                }
            });
        });
    }
}
